// src/components/MediaUploadButton.tsx

import React, { useRef } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { useMediaUpload } from '../../hooks/useMediaUpload';

interface MediaUploadButtonProps {
  /** previewUrl is a local blob: URL of the selected file — usable immediately, no auth needed. */
  onUploadComplete: (fileId: string, previewUrl?: string) => void;
  onUploadError?: (error: string) => void;
  accept?: string;
  theme?: {
    accentColor?: string;
    textColor?: string;
  };
  className?: string;
  showProgress?: boolean;
  buttonText?: string;
}

export const MediaUploadButton: React.FC<MediaUploadButtonProps> = ({
  onUploadComplete,
  onUploadError,
  accept = 'image/*,video/*',
  theme = {},
  className = '',
  showProgress = true,
  buttonText = 'Upload Media',
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isUploading, uploadProgress, processingStatus, error, uploadFile, cancelUpload } = useMediaUpload();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const fileId = await uploadFile(file);
      // The media route can't be fetched by an <img> until the file is public or
      // referenced (no Authorization header), so give callers a local preview.
      let previewUrl: string | undefined;
      try {
        previewUrl = URL.createObjectURL(file);
      } catch {
        previewUrl = undefined;
      }
      onUploadComplete(fileId, previewUrl);
    } catch (err: any) {
      const errorMsg = err.message || 'Upload failed';
      if (onUploadError) {
        onUploadError(errorMsg);
      }
    }

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleButtonClick = () => {
    if (isUploading) {
      cancelUpload();
    } else {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={className}>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        aria-hidden={isUploading}
        disabled={isUploading}
      />

      <button
        onClick={handleButtonClick}
        className="flex items-center gap-2 rounded-lg transition-all"
        style={{
          backgroundColor: isUploading ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
          color: theme.textColor || '#fff',
          border: `1px solid ${theme.accentColor || '#0891b2'}40`,
          padding: '6px 12px',
        }}
      >
        {isUploading ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            {processingStatus?.status === 'processing' ? 'Processing...' : 'Uploading...'}
          </>
        ) : (
          <>
            <Upload size={16} />
            {buttonText}
          </>
        )}
      </button>

      {showProgress && isUploading && (
        <div className="mt-2">
          <div aria-live="polite" className="sr-only">Upload in progress: {uploadProgress}%</div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span style={{ color: theme.textColor || '#fff' }}>
              {processingStatus?.status === 'processing' ? 'Processing' : 'Uploading'}
            </span>
            <span style={{ color: theme.textColor || '#fff' }}>
              {uploadProgress}%
            </span>
          </div>
          <div
            className="w-full h-2 rounded-full overflow-hidden"
            style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <div
              className="h-full transition-all duration-300"
              style={{
                width: `${uploadProgress}%`,
                backgroundColor: theme.accentColor || '#0891b2',
              }}
            />
          </div>
          {processingStatus && (
            <div className="text-xs mt-1" style={{ color: theme.textColor || '#fff', opacity: 0.7 }}>
              Status: {processingStatus.status}
            </div>
          )}
        </div>
      )}

      {error && (
        <div
          className="mt-2 px-3 py-2 rounded-lg text-sm"
          style={{
            backgroundColor: 'rgba(255, 0, 0, 0.2)',
            border: '1px solid rgba(255, 0, 0, 0.4)',
            color: theme.textColor || '#fff',
          }}
        >
          <div className="flex items-center justify-between">
            <span>❌ {error}</span>
            <button
              onClick={cancelUpload}
              className="ml-2"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
